"use client";

import * as React from "react";

import { cx } from "../../cx";
import styles from "./clamped-text.module.css";
import { useResizeMeasurement } from "./use-resize-measurement";

// A passive effect runs after paint, so a reused node would paint one frame
// with stale state. The layout effect is a no-op warning on the server.
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

export interface ClampedTextProps {
  children: string;
  /** Lines to show collapsed. */
  lines?: 2 | 3 | 4 | 5 | 6;
  moreLabel: string;
  lessLabel: string;
  /** Class for the text block — pass the surface's own type scale. */
  className?: string;
  /** Class for the more/less toggle. */
  toggleClassName?: string;
  /** Rendered between the text and its toggle (e.g. review thumbnails). */
  afterText?: React.ReactNode;
  "data-testid"?: string;
}

// With JS off the toggle never renders, so a <noscript> rule un-clamps the
// text instead of leaving it clipped with no way to read the rest. It goes in
// as a raw string: a JSX <style> child would be live on a client-only mount.
const NOSCRIPT_UNCLAMP_CLASS = "tc-clamped-text-body";
const NOSCRIPT_UNCLAMP_STYLE = `<style>.${NOSCRIPT_UNCLAMP_CLASS}{display:block!important;-webkit-line-clamp:unset!important;overflow:visible!important;}</style>`;

/**
 * Multi-line text that collapses to `lines` and offers a more/less toggle only
 * when the text is actually clipped. Overflow is measured
 * (`scrollHeight > clientHeight`), never guessed from the character count, and
 * re-measured on resize.
 */
export function ClampedText({
  children,
  lines = 3,
  moreLabel,
  lessLabel,
  className,
  toggleClassName,
  afterText,
  "data-testid": testId,
}: ClampedTextProps): React.ReactElement {
  const [overflowing, setOverflowing] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const elRef = React.useRef<HTMLParagraphElement | null>(null);

  // Only the collapsed box can tell whether the text overflows.
  const measure = React.useCallback((el: HTMLParagraphElement): void => {
    if (el.dataset["expanded"] === "true") return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, []);

  const setResizeRef = useResizeMeasurement(measure);
  const setRef = React.useCallback(
    (el: HTMLParagraphElement | null) => {
      elRef.current = el;
      setResizeRef(el);
    },
    [setResizeRef],
  );

  // New text on a reused node starts collapsed.
  useIsomorphicLayoutEffect(() => {
    setExpanded(false);
  }, [children]);

  // Re-measure whenever the box is collapsed again.
  useIsomorphicLayoutEffect(() => {
    if (!expanded && elRef.current) measure(elRef.current);
  }, [expanded, children, measure]);

  return (
    <>
      <noscript dangerouslySetInnerHTML={{ __html: NOSCRIPT_UNCLAMP_STYLE }} />
      <p
        ref={setRef}
        data-slot="clamped-text"
        data-expanded={expanded ? "true" : "false"}
        data-lines={expanded ? undefined : lines}
        data-testid={testId}
        className={cx(styles.text, className, NOSCRIPT_UNCLAMP_CLASS)}
      >
        {children}
      </p>
      {afterText}
      {overflowing && (
        <button
          type="button"
          data-tui=""
          data-slot="clamped-text-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className={cx(styles.toggle, toggleClassName)}
        >
          {expanded ? lessLabel : moreLabel}
        </button>
      )}
    </>
  );
}
